import * as React from 'react'
import { Loader2, Table2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  useArchiveIngestMappingConfigMutation,
  useGetIngestJobsPageQuery,
  useListIngestIntakesQuery,
  useListIngestMappingConfigsQuery,
  useUnarchiveIngestMappingConfigMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { Empty } from '../../../components/primitives/Empty'
import { Card } from '../../../components/ui/card'
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group'
import { ArchiveConfigDialog } from '../../TabularConfigPage/dialogs/ArchiveConfigDialog'
import { UnarchiveConfigDialog } from '../../TabularConfigPage/dialogs/UnarchiveConfigDialog'
import {
  activeVersions,
  newestFirst,
  versionsToUnarchive
} from '../../TabularConfigPage/util/configView'
import { extractErrorMessage } from '../util/extractErrorMessage'
import { buildCell, configRows, lastRunCell, usedByCell, type ConfigRow } from './config-view'
import { ConfigsTable, type ConfigsTableRow } from './ConfigsTable'

interface ConfigsTabProps {
  /** Intake names link to the Intakes tab only for users who can see it. */
  linkIntakes: boolean
}

export function ConfigsTab({ linkIntakes }: ConfigsTabProps) {
  const configsQuery = useListIngestMappingConfigsQuery({
    kind: 'mapped_tabular_ingest',
    include_archived: true,
    limit: 500
  })
  // The building → draft transition happens server-side; the build job's
  // wake-ups follow it. A draft only settles through a review on this
  // client, whose mutation refreshes the list.
  const buildsQuery = useGetIngestJobsPageQuery({
    kind: 'mapped_tabular_ingest_build',
    page: 0,
    pageSize: 100
  })
  const runsQuery = useGetIngestJobsPageQuery({
    kind: 'mapped_tabular_ingest',
    page: 0,
    pageSize: 100
  })
  const intakesQuery = useListIngestIntakesQuery()
  const [archiveConfig] = useArchiveIngestMappingConfigMutation()
  const [unarchiveConfig] = useUnarchiveIngestMappingConfigMutation()
  const [filter, setFilter] = React.useState<'active' | 'all'>('active')
  // The row stays set after the dialog closes so its copy holds through the close animation.
  const [archiving, setArchiving] = React.useState<ConfigRow | null>(null)
  const [archiveOpen, setArchiveOpen] = React.useState(false)
  const [archiveBusy, setArchiveBusy] = React.useState(false)
  const [archiveError, setArchiveError] = React.useState<string | null>(null)
  const [unarchiving, setUnarchiving] = React.useState<ConfigRow | null>(null)
  const [unarchiveOpen, setUnarchiveOpen] = React.useState(false)
  const [unarchiveBusy, setUnarchiveBusy] = React.useState(false)
  const [unarchiveError, setUnarchiveError] = React.useState<string | null>(null)

  const configs = React.useMemo(() => configRows(configsQuery.data ?? []), [configsQuery.data])
  const builds = buildsQuery.isError ? undefined : buildsQuery.data?.jobs
  const runs = runsQuery.isError ? undefined : runsQuery.data?.jobs
  const intakes = intakesQuery.isError ? undefined : intakesQuery.data?.intakes

  const rows = React.useMemo<ConfigsTableRow[]>(
    () =>
      configs.map((config) => ({
        config,
        ...(builds
          ? {
              build: buildCell(
                config.name,
                builds,
                config.archived ? undefined : config.latest.version
              )
            }
          : {}),
        ...(intakes ? { usedBy: usedByCell(config.name, intakes) } : {}),
        ...(runs ? { lastRun: lastRunCell(config.name, runs) } : {})
      })),
    [builds, configs, intakes, runs]
  )

  const activeRows = rows.filter((row) => !row.config.archived)
  const archivedRows = rows.filter((row) => row.config.archived)
  const unavailable = [
    buildsQuery.isError ? 'Builds' : null,
    intakesQuery.isError ? 'Intakes' : null,
    runsQuery.isError ? 'Runs' : null
  ].filter((label): label is string => label !== null)

  const openArchive = (config: ConfigRow) => {
    setArchiveError(null)
    setArchiving(config)
    setArchiveOpen(true)
  }

  const confirmArchive = async () => {
    if (!archiving) return
    setArchiveBusy(true)
    setArchiveError(null)
    try {
      for (const row of newestFirst(activeVersions(archiving.versions))) {
        await archiveConfig(row.id).unwrap()
      }
      toast.success(`${archiving.name} archived`)
      setArchiveOpen(false)
    } catch (error) {
      setArchiveError(extractErrorMessage(error, 'The config could not be archived.'))
    } finally {
      setArchiveBusy(false)
    }
  }

  const openUnarchive = (config: ConfigRow) => {
    setUnarchiveError(null)
    setUnarchiving(config)
    setUnarchiveOpen(true)
  }

  const confirmUnarchive = async () => {
    if (!unarchiving) return
    setUnarchiveBusy(true)
    setUnarchiveError(null)
    try {
      for (const row of versionsToUnarchive(unarchiving.versions)) {
        await unarchiveConfig(row.id).unwrap()
      }
      toast.success(`${unarchiving.name} unarchived`)
      setUnarchiveOpen(false)
    } catch (error) {
      setUnarchiveError(extractErrorMessage(error, 'The config could not be unarchived.'))
    } finally {
      setUnarchiveBusy(false)
    }
  }

  return (
    <>
      {configsQuery.isLoading ? (
        <Card className="flex flex-row items-center gap-2 px-4 py-6 text-sm text-ink-3">
          <Loader2 className="size-4 animate-spin" />
          Loading tabular configs…
        </Card>
      ) : configsQuery.isError ? (
        <Card className="gap-0 px-4 py-4">
          <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
            Tabular configs are unavailable.{' '}
            <button type="button" className="underline" onClick={() => void configsQuery.refetch()}>
              Try again
            </button>
          </div>
        </Card>
      ) : configs.length === 0 ? (
        <Card className="gap-0 py-0">
          <Empty
            icon={Table2}
            title="No tabular configs yet"
            hint="Start a New Ingest from the Runs tab with a tabular source to build one from a few example files."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={filter}
              onValueChange={(value) => {
                if (value === 'active' || value === 'all') setFilter(value)
              }}
              aria-label="Config filter"
            >
              <ToggleGroupItem value="active" className="h-7 px-2.5 text-11 font-medium">
                Active
              </ToggleGroupItem>
              <ToggleGroupItem value="all" className="h-7 px-2.5 text-11 font-medium">
                All
              </ToggleGroupItem>
            </ToggleGroup>
            {unavailable.length > 0 ? (
              <p className="ml-auto text-xs text-ink-3">{unavailable.join(', ')} unavailable.</p>
            ) : null}
          </div>
          <ConfigsTable
            rows={activeRows}
            archivedRows={filter === 'all' ? archivedRows : []}
            linkIntakes={linkIntakes}
            onArchive={openArchive}
            onUnarchive={openUnarchive}
          />
        </div>
      )}

      <ArchiveConfigDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (!open && !archiveBusy) setArchiveOpen(false)
        }}
        name={archiving?.name ?? ''}
        activeVersions={archiving ? activeVersions(archiving.versions).length : 0}
        loading={archiveBusy}
        error={archiveError ?? undefined}
        onConfirm={() => void confirmArchive()}
      />
      <UnarchiveConfigDialog
        open={unarchiveOpen}
        onOpenChange={(open) => {
          if (!open && !unarchiveBusy) setUnarchiveOpen(false)
        }}
        name={unarchiving?.name ?? ''}
        versionCount={unarchiving ? versionsToUnarchive(unarchiving.versions).length : 0}
        loading={unarchiveBusy}
        error={unarchiveError ?? undefined}
        onConfirm={() => void confirmUnarchive()}
      />
    </>
  )
}
